<script setup>
import { h, ref, watch, computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import Switch from 'next/switch/Switch.vue';
import DurationInput from 'next/input/DurationInput.vue';
import SingleSelect from 'dashboard/components-next/filter/inputs/SingleSelect.vue';
import { DURATION_UNITS } from 'dashboard/components-next/input/constants';

const { t } = useI18n();
const duration = ref(0);
const unit = ref(DURATION_UNITS.MINUTES);
const message = ref('');
const labelToApply = ref(null);
const ignoreWaiting = ref(false);
const isEnabled = ref(false);

const pendingDuration = ref(0);
const pendingUnit = ref(DURATION_UNITS.MINUTES);
const pendingMessage = ref('');
const isEnabledPending = ref(false);
const isPendingInitialized = ref(false);

const { currentAccount } = useAccount();

const labels = useMapGetter('labels/getLabels');

const labelOptions = computed(() =>
  labels.value?.length
    ? labels.value.map(label => ({
        id: label.title,
        name: label.title,
        icon: h('span', {
          class: `size-[12px] ring-1 ring-n-alpha-1 dark:ring-white/20 ring-inset rounded-sm`,
          style: { backgroundColor: label.color },
        }),
      }))
    : []
);

const selectedLabelName = computed(() => {
  return labelToApply.value?.name ?? null;
});

watch(
  [currentAccount, labelOptions],
  () => {
    const {
      auto_resolve_after,
      auto_resolve_message,
      auto_resolve_ignore_waiting,
      auto_resolve_label,
      auto_resolve_pending_after,
      auto_resolve_pending_message,
    } = currentAccount.value?.settings || {};

    duration.value = auto_resolve_after;
    message.value = auto_resolve_message;
    ignoreWaiting.value = auto_resolve_ignore_waiting;
    labelToApply.value =
      labelOptions.value.find(option => option.name === auto_resolve_label) ??
      null;

    if (duration.value) {
      if (duration.value % (24 * 60) === 0) {
        unit.value = DURATION_UNITS.DAYS;
      } else if (duration.value % 60 === 0) {
        unit.value = DURATION_UNITS.HOURS;
      } else {
        unit.value = DURATION_UNITS.MINUTES;
      }

      isEnabled.value = true;
    }

    pendingDuration.value = auto_resolve_pending_after;

    if (!isPendingInitialized.value) {
      pendingMessage.value = auto_resolve_pending_message || '';
      isPendingInitialized.value = true;
    }

    if (pendingDuration.value) {
      if (pendingDuration.value % (24 * 60) === 0) {
        pendingUnit.value = DURATION_UNITS.DAYS;
      } else if (pendingDuration.value % 60 === 0) {
        pendingUnit.value = DURATION_UNITS.HOURS;
      } else {
        pendingUnit.value = DURATION_UNITS.MINUTES;
      }

      isEnabledPending.value = true;
    }
  },
  { deep: true, immediate: true }
);

const validate = () => {
  if (isEnabled.value && (!duration.value || duration.value < 10)) {
    useAlert(t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.DURATION.ERROR'));
    return false;
  }
  if (isEnabledPending.value && pendingDuration.value < 10) {
    useAlert(t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE_PENDING.DURATION.ERROR'));
    return false;
  }
  return true;
};

const getSettings = () => {
  return {
    auto_resolve_after: isEnabled.value ? duration.value : null,
    auto_resolve_message: message.value,
    auto_resolve_ignore_waiting: ignoreWaiting.value,
    auto_resolve_label: selectedLabelName.value,
    auto_resolve_pending_after: isEnabledPending.value
      ? pendingDuration.value
      : null,
    auto_resolve_pending_message: pendingMessage.value,
  };
};

const toggleAutoResolve = () => {
  if (!isEnabled.value) {
    duration.value = null;
    message.value = '';
    ignoreWaiting.value = false;
    labelToApply.value = null;
  }
};

const togglePendingAutoResolve = () => {
  if (!isEnabledPending.value) {
    pendingDuration.value = null;
    pendingMessage.value = '';
  }
};

defineExpose({ getSettings, validate });
</script>

<template>
  <div class="flex flex-col gap-3">
    <!-- Auto Resolve -->
    <div class="grid grid-cols-2 gap-3">
      <button
        type="button"
        class="flex flex-col gap-1 rounded-xl border px-4 py-3 text-left transition-all duration-200 cursor-pointer"
        :class="
          !isEnabled
            ? 'border-[rgba(74,222,128,0.5)] bg-[rgba(74,222,128,0.05)] shadow-[0_0_16px_rgba(74,222,128,0.15)]'
            : 'border-white/10 bg-white/5 hover:border-white/20'
        "
        @click="
          isEnabled = false;
          toggleAutoResolve();
        "
      >
        <span
          class="text-[10px] font-semibold tracking-[0.15em] uppercase"
          :class="!isEnabled ? 'text-[#4ade80]' : 'text-n-slate-10'"
        >
          {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.DISABLED_LABEL') }}
        </span>
        <span class="text-xs text-n-slate-9">
          {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.DISABLED_NOTE') }}
        </span>
      </button>
      <button
        type="button"
        class="flex flex-col gap-1 rounded-xl border px-4 py-3 text-left transition-all duration-200 cursor-pointer"
        :class="
          isEnabled
            ? 'border-[rgba(74,222,128,0.5)] bg-[rgba(74,222,128,0.05)] shadow-[0_0_16px_rgba(74,222,128,0.15)]'
            : 'border-white/10 bg-white/5 hover:border-white/20'
        "
        @click="isEnabled = true"
      >
        <span
          class="text-[10px] font-semibold tracking-[0.15em] uppercase"
          :class="isEnabled ? 'text-[#4ade80]' : 'text-n-slate-10'"
        >
          {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.ENABLED_LABEL') }}
        </span>
        <span class="text-xs text-n-slate-9">
          {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.ENABLED_NOTE') }}
        </span>
      </button>
    </div>

    <template v-if="isEnabled">
      <div
        class="flex flex-col gap-1 rounded-xl border border-white/10 bg-white/5 px-4 pt-1.5 pb-2 transition-all duration-200 focus-within:border-[rgba(74,222,128,0.5)] focus-within:shadow-[0_0_16px_rgba(74,222,128,0.2)]"
      >
        <span
          class="text-[10px] font-semibold tracking-[0.15em] text-n-slate-10 uppercase"
        >
          {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.DURATION.LABEL') }}
        </span>
        <DurationInput
          v-model="duration"
          v-model:unit="unit"
          :min="0"
          :max="1438560"
        />
        <span class="text-[10px] text-n-slate-9 mt-0.5">
          {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.DURATION.HELP') }}
        </span>
      </div>

      <div
        class="flex flex-col gap-1 rounded-xl border border-white/10 bg-white/5 px-4 pt-1.5 pb-2 transition-all duration-200 focus-within:border-[rgba(74,222,128,0.5)] focus-within:shadow-[0_0_16px_rgba(74,222,128,0.2)]"
      >
        <span
          class="text-[10px] font-semibold tracking-[0.15em] text-n-slate-10 uppercase"
        >
          {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.MESSAGE.LABEL') }}
        </span>
        <textarea
          v-model="message"
          rows="3"
          :placeholder="
            t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.MESSAGE.PLACEHOLDER')
          "
          class="bg-transparent border-0 outline-none text-sm text-n-slate-9 placeholder:text-n-slate-8 p-0 resize-none mt-1"
        />
      </div>

      <div
        class="flex items-center justify-between rounded-xl border border-white/10 bg-white/5 px-4 py-3 transition-all duration-200 hover:border-white/20"
      >
        <span
          class="text-[10px] font-semibold tracking-[0.15em] text-n-slate-10 uppercase"
        >
          {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.IGNORE_WAITING.LABEL') }}
        </span>
        <Switch v-model="ignoreWaiting" />
      </div>

      <div
        class="flex items-center justify-between rounded-xl border border-white/10 bg-white/5 px-4 py-3 transition-all duration-200 hover:border-white/20"
      >
        <span
          class="text-[10px] font-semibold tracking-[0.15em] text-n-slate-10 uppercase"
        >
          {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.LABEL.LABEL') }}
        </span>
        <SingleSelect
          v-model="labelToApply"
          :options="labelOptions"
          :placeholder="
            t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.LABEL.PLACEHOLDER')
          "
          placeholder-icon="i-lucide-chevron-down"
          placeholder-trailing-icon
          variant="faded"
        />
      </div>
    </template>

    <!-- Auto Resolve Pending -->
    <div class="grid grid-cols-2 gap-3 mt-4">
      <button
        type="button"
        class="flex flex-col gap-1 rounded-xl border px-4 py-3 text-left transition-all duration-200 cursor-pointer"
        :class="
          !isEnabledPending
            ? 'border-[rgba(74,222,128,0.5)] bg-[rgba(74,222,128,0.05)] shadow-[0_0_16px_rgba(74,222,128,0.15)]'
            : 'border-white/10 bg-white/5 hover:border-white/20'
        "
        @click="
          isEnabledPending = false;
          togglePendingAutoResolve();
        "
      >
        <span
          class="text-[10px] font-semibold tracking-[0.15em] uppercase"
          :class="!isEnabledPending ? 'text-[#4ade80]' : 'text-n-slate-10'"
        >
          {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE_PENDING.DISABLED_LABEL') }}
        </span>
        <span class="text-xs text-n-slate-9">
          {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE_PENDING.DISABLED_NOTE') }}
        </span>
      </button>
      <button
        type="button"
        class="flex flex-col gap-1 rounded-xl border px-4 py-3 text-left transition-all duration-200 cursor-pointer"
        :class="
          isEnabledPending
            ? 'border-[rgba(74,222,128,0.5)] bg-[rgba(74,222,128,0.05)] shadow-[0_0_16px_rgba(74,222,128,0.15)]'
            : 'border-white/10 bg-white/5 hover:border-white/20'
        "
        @click="isEnabledPending = true"
      >
        <span
          class="text-[10px] font-semibold tracking-[0.15em] uppercase"
          :class="isEnabledPending ? 'text-[#4ade80]' : 'text-n-slate-10'"
        >
          {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE_PENDING.ENABLED_LABEL') }}
        </span>
        <span class="text-xs text-n-slate-9">
          {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE_PENDING.ENABLED_NOTE') }}
        </span>
      </button>
    </div>

    <template v-if="isEnabledPending">
      <div
        class="flex flex-col gap-1 rounded-xl border border-white/10 bg-white/5 px-4 pt-1.5 pb-2 transition-all duration-200 focus-within:border-[rgba(74,222,128,0.5)] focus-within:shadow-[0_0_16px_rgba(74,222,128,0.2)]"
      >
        <span
          class="text-[10px] font-semibold tracking-[0.15em] text-n-slate-10 uppercase"
        >
          {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE_PENDING.DURATION.LABEL') }}
        </span>
        <DurationInput
          v-model="pendingDuration"
          v-model:unit="pendingUnit"
          :min="0"
          :max="1438560"
        />
        <span class="text-[10px] text-n-slate-9 mt-0.5">
          {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE_PENDING.DURATION.HELP') }}
        </span>
      </div>

      <div
        class="flex flex-col gap-1 rounded-xl border border-white/10 bg-white/5 px-4 pt-1.5 pb-2 transition-all duration-200 focus-within:border-[rgba(74,222,128,0.5)] focus-within:shadow-[0_0_16px_rgba(74,222,128,0.2)]"
      >
        <span
          class="text-[10px] font-semibold tracking-[0.15em] text-n-slate-10 uppercase"
        >
          {{ t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE_PENDING.MESSAGE.LABEL') }}
        </span>
        <textarea
          v-model="pendingMessage"
          rows="3"
          :placeholder="
            t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE_PENDING.MESSAGE.PLACEHOLDER')
          "
          class="bg-transparent border-0 outline-none text-sm text-n-slate-9 placeholder:text-n-slate-8 p-0 resize-none mt-1"
        />
      </div>
    </template>
  </div>
</template>
